import Lottie from "lottie-react";
import { useEffect, useState } from "react";
import { isMobile, isTablet } from "react-device-detect";
import ReactPlayer from "react-player";
import styled from "styled-components";
import { VirtualSpace } from "@lifeat/shared/data-models/virtual-space/VirtualSpace";
import { SpaceSource, inferSpaceSource } from "@lifeat/shared/spaces/SpacesUtil";
import { useEvent } from "@hooks/useEvent";
import LoadingAnimation from "@resources/animations/loading-animation.json";
import { ScrollbarStyles } from "@themes/ScrollbarStyles";
import Styles from "@themes/Styles";
import { useAppModeContext } from "../../../../../app/LifeAtApp/AppModeContext";
import PricingPageContent from "../../../../upgrade-flow/components/PricingPageContent";
import SpaceView from "../SpaceView";
import "./SpacePlayerView.scss";

const PRO_SPACE_PREVIEW_TIME = 5000;

const ImageSpace = styled.img`
    width: 100vw;
    height: 100vh;
    object-fit: cover;
`;

const StyledLottieDiv = styled(Lottie)`
    scale: 0.2;
`;

const PricingModal = styled.div<{ showModal: boolean }>`
    opacity: ${(props) => (props.showModal ? "1" : "0")};
    background-color: ${(props) => props.theme.backgroundPrimary};

    max-height: calc(100% - 50px - ${Styles.topBarAndPaddingHeight} - ${Styles.bottomNavigationHeight});
    margin-bottom: 20px; // To make the modal appear centered with the bottom nav

    overflow-y: scroll;
    border-radius: 10px;

    transition: opacity 500ms ease-in-out;

    scrollbar-gutter: stable both-edges;
    ${ScrollbarStyles};
`;

const StyledPricingPageContent = styled(PricingPageContent)`
    padding: 20px;
`;

interface SpacePlayerViewProps {
    /**
     * The source for the video. For YouTube videos, this is the embed URL.
     * For Cloudflare videos, this is the video UID (the actual URL is
     * dynamically constructed in this component).
     */
    readonly videoUrl: VirtualSpace["videoUrl"];
    /**
     * Value from 0.0 (effectively muted) to 1.0 (max volume)
     */
    readonly volume: number;
    /**
     * is space blocked for free users
     */
    readonly isBlockedSpace: boolean;
    /**
     * Space category
     */
    readonly category: VirtualSpace["category"];
    /**
     * Space name
     */
    readonly spaceName: VirtualSpace["name"];
    className?: string;
}
export default function SpacePlayerView(props: SpacePlayerViewProps) {
    const source = inferSpaceSource(props.videoUrl);
    const { appMode } = useAppModeContext();

    const [showPricingModal, setShowPricingModal] = useState(false);

    const showPricingAfterPreview = useEvent(() => {
        const timeout = setTimeout(() => setShowPricingModal(true), PRO_SPACE_PREVIEW_TIME);
        return () => clearTimeout(timeout);
    });

    useEffect(() => {
        // Reset pricing modal visibility.
        setShowPricingModal(false);

        if (props.isBlockedSpace) {
            return showPricingAfterPreview();
        }
    }, [props.videoUrl, props.isBlockedSpace, showPricingAfterPreview]);

    const playing = !showPricingModal && appMode === "focus";

    let player;
    if (props.videoUrl) {
        switch (source) {
            case SpaceSource.YOUTUBE:
                player = (
                    <ReactPlayer
                        url={props.videoUrl}
                        width="100vw"
                        height="100vh"
                        playing={playing}
                        loop={true}
                        volume={props.volume}
                        muted={props.volume === 0}
                        playsinline={true}
                    />
                );
                break;
            case SpaceSource.CLOUDFLARE_VIDEO:
                if (isMobile || isTablet) {
                    // Cloudflare Stream react component audio does not work on mobile (at all).
                    // Use the HLS video src with React Player (does not give metrics).
                    player = (
                        <ReactPlayer
                            url={buildCloudflareUrlForMobile(props.videoUrl)}
                            width="100vw"
                            height="100vh"
                            playing={playing}
                            loop={true}
                            volume={props.volume}
                            muted={props.volume === 0}
                            playsinline={true}
                        />
                    );
                } else {
                    // Use the Stream React component for users on Desktop (gives us metrics).
                    player = (
                        <ReactPlayer
                            url={buildCloudflareUrlForDesktop(props.videoUrl)}
                            width="100vw"
                            height="100vh"
                            playing={playing}
                            loop={true}
                            volume={props.volume}
                            muted={props.volume === 0}
                            playsinline={true}
                        />
                    );
                }
                break;
            case SpaceSource.CLOUDFLARE_IMAGE:
                player = <ImageSpace src={props.videoUrl} />;
                break;
        }
    }

    return (
        <div className={`video-player ${props.className}`}>
            <span className="loadScreen">
                <StyledLottieDiv
                    animationData={LoadingAnimation}
                    height={"100px"}
                    width={"100px"}
                    style={{ position: "absolute", pointerEvents: "none" }}
                    loop={true}
                    autoplay={true}
                />
            </span>

            <SpaceView isVisible={true} frameObj={player} />

            <div className={`video-overlay`}>
                <div className={`video-overlay-blur ${showPricingModal && `show-blur`}`} />

                <PricingModal showModal={showPricingModal}>
                    <StyledPricingPageContent
                        upgradeContext={"spaces"}
                        hideFreeTier={true}
                        hideTestimonials={true}
                        amplitudeEventAdditionalSourceData={{
                            spaceCategory: props.category,
                            spaceName: props.spaceName,
                        }}
                    />
                </PricingModal>
            </div>
        </div>
    );
}

/**
 * Build the Cloudflare video src URL for using the Stream React component
 * for desktops.
 * @param {number} cloudflareVideoUid The video UID.
 */
const buildCloudflareUrlForDesktop = (cloudflareVideoUid: string) => {
    // return `https://videodelivery.net/${cloudflareVideoUid}/manifest/video.m3u8`;
    return `https://cloudflarestream.com/${cloudflareVideoUid}/manifest/video.m3u8`;
};

/**
 * Build the Cloudflare video src URL for mobile devices using the HLS video
 * (.m3u8 extension; .mpd is for DASH).
 * @param {number} cloudflareVideoUid The video UID.
 */
const buildCloudflareUrlForMobile = (cloudflareVideoUid: string) => {
    // return `https://videodelivery.net/${cloudflareVideoUid}/manifest/video.m3u8`;
    return `https://cloudflarestream.com/${cloudflareVideoUid}/manifest/video.m3u8`;
};
